import { useCallback, useEffect, useRef, useState } from "react"

import {
  useTransportInfiniteQuery,
  useTransportMutation,
  useTransportQueryUtils,
} from "@/transport"

import type { CreateOrganizationTagRequest } from "@/types_api/backend/transport/create_organization_tag_request"
import type { UpdateOrganizationTagRequest } from "@/types_api/backend/transport/update_organization_tag_request"

import { TAGGED_RESOURCE_OPERATIONS } from "../constants/tagged-resource-operations"

export function useManageTagCatalog(search: string) {
  const query = useTransportInfiniteQuery({
    operation: "listWorkspaceTags",
    request: {
      include_usage: true,
      page_size: 100,
      search,
    },
    getNextRequest: (lastPage, _pages, previousRequest) =>
      lastPage.next_page_token && previousRequest
        ? { ...previousRequest, page_token: lastPage.next_page_token }
        : null,
    options: { throwOnError: false, staleTime: 0, refetchOnMount: "always" },
  })
  const queryUtils = useTransportQueryUtils()
  const createMutation = useTransportMutation("createOrganizationTag")
  const updateMutation = useTransportMutation("updateOrganizationTag")
  const deleteMutation = useTransportMutation("deleteOrganizationTag")
  const mounted = useRef(true)
  const pending = useRef(false)
  const [isPending, setIsPending] = useState(false)
  const refreshing = useRef(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const refresh = useCallback(async () => {
    if (refreshing.current) {
      return
    }
    refreshing.current = true
    if (mounted.current) {
      setIsRefreshing(true)
    }
    try {
      await Promise.all([
        queryUtils.invalidateOperation("listOrganizationTags"),
        queryUtils.invalidateOperation("listWorkspaceTags"),
        ...TAGGED_RESOURCE_OPERATIONS.map((operationName) =>
          queryUtils.invalidateOperation(operationName),
        ),
      ])
      if (mounted.current) {
        setRefreshError(null)
      }
    } catch {
      if (mounted.current) {
        setRefreshError(
          "Tag data could not be refreshed. Retry refreshing before making more changes.",
        )
      }
    } finally {
      refreshing.current = false
      if (mounted.current) {
        setIsRefreshing(false)
      }
    }
  }, [queryUtils])

  const runMutation = useCallback(
    async (operation: () => Promise<unknown>) => {
      if (
        pending.current ||
        refreshing.current ||
        refreshError ||
        !mounted.current
      ) {
        return false
      }
      pending.current = true
      setIsPending(true)
      setError(null)
      let saved = false
      try {
        await operation()
        saved = true
      } catch (cause) {
        if (mounted.current) {
          setError(
            cause instanceof Error
              ? cause.message
              : "The tag could not be saved. Please try again.",
          )
        }
      } finally {
        await refresh()
        pending.current = false
        if (mounted.current) {
          setIsPending(false)
        }
      }
      return saved && mounted.current
    },
    [refresh, refreshError],
  )

  return {
    query,
    isPending: isPending || isRefreshing,
    error,
    refreshError,
    refresh,
    clearError: () => setError(null),
    isReady:
      createMutation.isReady &&
      updateMutation.isReady &&
      deleteMutation.isReady &&
      !refreshError,
    create: (request: CreateOrganizationTagRequest) =>
      runMutation(() => createMutation.mutateAsync(request)),
    update: (request: UpdateOrganizationTagRequest) =>
      runMutation(() => updateMutation.mutateAsync(request)),
    deleteTag: (tagId: string) =>
      runMutation(() => deleteMutation.mutateAsync({ tag_id: tagId })),
  }
}
